import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface RatingWidgetProps {
  decisionId: string | null;
  onRated?: (rating: number) => void;
  variant?: "thumbs" | "stars";
  className?: string;
}

const RatingWidget = ({ decisionId, onRated, variant = "thumbs", className }: RatingWidgetProps) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitRating = async (value: number) => {
    if (!decisionId) {
      console.log("No decision ID, skipping rating submission");
      setRating(value);
      onRated?.(value);
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("track-outcome", {
        body: {
          decisionId,
          outcome: value >= 4 ? "success" : value >= 3 ? "partial" : "fail",
          outcomeScore: value,
          signal: "rating",
        },
      });

      if (error) throw error;
      
      setRating(value);
      onRated?.(value);
      toast.success("Thanks for your feedback!");
    } catch (error) {
      console.error("Failed to submit rating:", error);
      // Still show the rating locally even if submission fails
      setRating(value);
      onRated?.(value);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (variant === "stars") {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => submitRating(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(null)}
            disabled={isSubmitting || rating !== null}
            className="p-0.5 transition-transform hover:scale-110 disabled:opacity-50"
          >
            <Star
              className={cn(
                "h-5 w-5 transition-colors",
                (hoveredStar !== null ? star <= hoveredStar : star <= (rating || 0))
                  ? "fill-warning text-warning"
                  : "text-muted-foreground"
              )}
            />
          </button>
        ))}
        {rating && (
          <span className="ml-2 text-sm text-muted-foreground">
            {rating}/5
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span className="text-sm text-muted-foreground mr-1">Was this helpful?</span>
      <Button
        variant={rating === 5 ? "default" : "outline"}
        size="sm"
        onClick={() => submitRating(5)}
        disabled={isSubmitting || rating !== null}
        className={cn(
          rating === 5 && "bg-success hover:bg-success/90 border-success"
        )}
      >
        <ThumbsUp className="h-4 w-4" />
      </Button>
      <Button
        variant={rating === 1 ? "default" : "outline"}
        size="sm"
        onClick={() => submitRating(1)}
        disabled={isSubmitting || rating !== null}
        className={cn(
          rating === 1 && "bg-destructive hover:bg-destructive/90 border-destructive"
        )}
      >
        <ThumbsDown className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default RatingWidget;
